import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Truck, RotateCcw, ShieldCheck, Star, Hammer, TreePine, Gem } from 'lucide-react';
import FeaturedCarousel from '../components/FeaturedCarousel';

const TrustBanner = () => (
  <section className="py-6 md:py-8 bg-white" data-testid="trust-banner">
    <div className="max-w-5xl mx-auto px-6 md:px-12">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
        <div className="flex flex-col items-center text-center gap-2" data-testid="trust-badge-payment">
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#A08850' }}>
            <ShieldCheck className="w-5 h-5 text-[#1B2A4A]" />
          </div>
          <span className="text-xs md:text-sm font-medium text-[#1B2A4A]" style={{ fontFamily: 'Playfair Display, serif' }}>Secure Payment</span>
        </div>
        <div className="flex flex-col items-center text-center gap-2" data-testid="trust-badge-service">
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#A08850' }}>
            <Star className="w-5 h-5 text-[#1B2A4A]" />
          </div>
          <span className="text-xs md:text-sm font-medium text-[#1B2A4A]" style={{ fontFamily: 'Playfair Display, serif' }}>Excellent Service</span>
        </div>
        <div className="flex flex-col items-center text-center gap-2" data-testid="trust-badge-return">
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#A08850' }}>
            <RotateCcw className="w-5 h-5 text-[#1B2A4A]" />
          </div>
          <span className="text-xs md:text-sm font-medium text-[#1B2A4A]" style={{ fontFamily: 'Playfair Display, serif' }}>30-Day Return</span>
        </div>
        <div className="flex flex-col items-center text-center gap-2" data-testid="trust-badge-shipping">
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#A08850' }}>
            <Truck className="w-5 h-5 text-[#1B2A4A]" />
          </div>
          <span className="text-xs md:text-sm font-medium text-[#1B2A4A]" style={{ fontFamily: 'Playfair Display, serif' }}>Free Shipping</span>
        </div>
      </div>
    </div>
  </section>
);

const Home = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.state?.scrollTo === 'collections') {
      const scrollToCollections = () => {
        const el = document.getElementById('collections');
        if (el) el.scrollIntoView({ behavior: 'auto', block: 'start' });
      };
      scrollToCollections();
      setTimeout(scrollToCollections, 300);
    }
  }, [location.state]);

  return (
    <div data-testid="home-page">
      {/* Featured Products Carousel — Hero */}
      <section className="pt-6 pb-12 md:pt-8 md:pb-16 bg-[#F5F2EB]" data-testid="featured-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-6xl font-medium mb-4" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="featured-title">
              Featured Products
            </h1>
            <p className="text-lg text-[#5D4037]" data-testid="featured-subtitle">
              Handpicked from our artisan collections
            </p>
          </div>
          <FeaturedCarousel />
        </div>
      </section>

      {/* Trust Banner — below hero */}
      <TrustBanner />

      {/* Our Collections */}
      <section className="py-20 md:py-32 bg-[#EBE5DA]" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 id="collections" className="text-4xl md:text-5xl font-medium text-center mb-16 scroll-mt-24" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="categories-title">
            Our Collections
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
            <Link to="/woodwork-and-art" className="group" data-testid="category-link-woodwork-and-art">
              <div className="bg-white/40 border border-[#D7C0A0] overflow-hidden transition-all duration-500 hover:bg-white hover:shadow-xl h-full flex flex-col">
                <div className="aspect-square overflow-hidden">
                  <img src="https://customer-assets.emergentagent.com/job_c40d2a50-2ea7-48bd-927c-1b6d899d3de9/artifacts/ep8ycqse_Woodwork%20and%20Art%20Category%20Tile%20Picture.webp" alt="Woodwork & Art" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6 flex-1">
                  <h3 className="text-2xl font-medium mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Woodwork & Art</h3>
                  <p className="text-[#5D4037]">Handcrafted wooden products and artisan artwork</p>
                </div>
              </div>
            </Link>
            <Link to="/mandirs" className="group" data-testid="category-link-mandirs">
              <div className="bg-white/40 border border-[#D7C0A0] overflow-hidden transition-all duration-500 hover:bg-white hover:shadow-xl h-full flex flex-col">
                <div className="aspect-square overflow-hidden">
                  <img src="https://customer-assets.emergentagent.com/job_c40d2a50-2ea7-48bd-927c-1b6d899d3de9/artifacts/5sstsl06_Bespoke%20Mandirs%20Category%20Tile%20Picture%20%281%29.webp" alt="Bespoke Mandirs" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6 flex-1">
                  <h3 className="text-2xl font-medium mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Bespoke Mandirs</h3>
                  <p className="text-[#5D4037]">Sacred home temples for prayer and meditation</p>
                </div>
              </div>
            </Link>
            <Link to="/copperware" className="group" data-testid="category-link-copperware">
              <div className="bg-white/40 border border-[#D7C0A0] overflow-hidden transition-all duration-500 hover:bg-white hover:shadow-xl h-full flex flex-col">
                <div className="aspect-square overflow-hidden">
                  <img src="https://customer-assets.emergentagent.com/job_c40d2a50-2ea7-48bd-927c-1b6d899d3de9/artifacts/ucj6avqz_Artisan%20Metals%20Category%20Tile%20Picture.png" alt="Artisan Metals" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6 flex-1">
                  <h3 className="text-2xl font-medium mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Artisan Metals</h3>
                  <p className="text-[#5D4037]">Hammered metals with centuries of tradition</p>
                </div>
              </div>
            </Link>
            <Link to="/candles" className="group" data-testid="category-link-candles">
              <div className="bg-white/40 border border-[#D7C0A0] overflow-hidden transition-all duration-500 hover:bg-white hover:shadow-xl h-full flex flex-col">
                <div className="aspect-square overflow-hidden">
                  <img src="https://customer-assets.emergentagent.com/job_c40d2a50-2ea7-48bd-927c-1b6d899d3de9/artifacts/vvfm9lqn_Ayurvedic%20Aromas%20Category%20Tile%20Picture.webp" alt="Ayurvedic Aromas" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6 flex-1">
                  <h3 className="text-2xl font-medium mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Ayurvedic Aromas</h3>
                  <p className="text-[#5D4037]">Artisan candles with traditional fragrances</p>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Your Premier Destination Section */}
      <section className="py-20 md:py-28" style={{ backgroundColor: '#C4B69C' }} data-testid="premier-destination-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-center">
            <div className="overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_c2e293f8-1738-4ed6-a581-e197b34b7fa1/artifacts/ysmgxdrj_Photo%20of%20a%20Laser%20Cutting%20Machine%20in%20Action.jpg" 
                alt="Laser cutting intricate wood patterns" 
                className="w-full h-auto object-cover"
                data-testid="premier-destination-image"
              />
            </div>
            <div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#1B2A4A] leading-tight mb-8" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="premier-destination-title">
                Your Premier Destination for Divine Decor Rooted in Tradition.
              </h2>
              <p className="text-[#1B2A4A]/80 leading-relaxed mb-4 text-base" data-testid="premier-destination-intro">
                <strong>At Rasaa ~ The Essence, we believe in the beauty of tradition and the soul of craftsmanship.</strong>
              </p>
              <p className="text-[#1B2A4A]/70 leading-relaxed mb-10 text-base" data-testid="premier-destination-description">
                Rooted in Indian heritage, our handcrafted mandirs, candles, and custom gifts are designed to honor sacred moments and elevate everyday spaces. Whether you're creating a corner of devotion, searching for the perfect wedding favor, or adding warmth to your home, our collections bring meaning, elegance, and essence into every detail.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Voices of Rasaa - Testimonials */}
      <section className="py-10 md:py-14" style={{ backgroundColor: '#A89B8A' }} data-testid="testimonials-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="text-center mb-6">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1B2A4A] leading-tight mb-2" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="testimonials-title">
              Voices of Rasaa
            </h2>
            <p className="text-[#1B2A4A]/70 text-sm italic" style={{ fontFamily: 'Playfair Display, serif' }}>
              Loved by many, crafted for you—find out why we're a customer favorite.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="bg-white rounded-xl p-5 shadow-sm" data-testid="testimonial-1">
              <span className="text-2xl font-bold text-[#D4A843] leading-none" style={{ fontFamily: 'Georgia, serif' }}>"</span>
              <p className="text-[#1B2A4A]/70 leading-relaxed mt-2 mb-3 text-sm" style={{ fontFamily: 'Playfair Display, serif' }}>
                I had such a great experience working with them personally. They were always quick to respond, really listened to what I needed, and made sure everything was exactly how I wanted it.
              </p>
              <p className="text-[#1B2A4A] font-medium text-xs">~ Jayshree Pancholi</p>
            </div>
            <div className="bg-white rounded-xl p-5 shadow-sm" data-testid="testimonial-2">
              <span className="text-2xl font-bold text-[#D4A843] leading-none" style={{ fontFamily: 'Georgia, serif' }}>"</span>
              <p className="text-[#1B2A4A]/70 leading-relaxed mt-2 mb-3 text-sm" style={{ fontFamily: 'Playfair Display, serif' }}>
                I am absolutely thrilled with the service I received! They were attentive, responsive, and genuinely cared about meeting my needs. I highly recommend them.
              </p>
              <p className="text-[#1B2A4A] font-medium text-xs">~ Hamsa Moorthy</p>
            </div>
            <div className="bg-white rounded-xl p-5 shadow-sm" data-testid="testimonial-3">
              <span className="text-2xl font-bold text-[#D4A843] leading-none" style={{ fontFamily: 'Georgia, serif' }}>"</span>
              <p className="text-[#1B2A4A]/70 leading-relaxed mt-2 mb-3 text-sm" style={{ fontFamily: 'Playfair Display, serif' }}>
                5/5 Beautiful, well-crafted shelf. Bought my daughter the Lennox spice set. She liked the traditional look. It is really well-made, very solid. Thank you!
              </p>
              <p className="text-[#1B2A4A] font-medium text-xs">~ Shannon</p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Process Section */}
      <section className="py-14 md:py-20 bg-[#2C1810]" data-testid="our-process-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <header className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-medium text-[#D4A843] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              Our Process
            </h2>
            <p className="text-white/70 text-sm max-w-2xl mx-auto">Every piece begins with intention and ends with perfection.</p>
          </header>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <article className="text-center">
              <TreePine className="w-8 h-8 text-[#D4A843] mx-auto mb-3" />
              <h3 className="text-lg font-medium text-white mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Premium Woods</h3>
              <p className="text-white/60 text-sm leading-relaxed">Sustainably sourced hardwoods — walnut, oak, and pine — selected for grain quality, durability, and natural beauty.</p>
            </article>
            <article className="text-center">
              <Hammer className="w-8 h-8 text-[#D4A843] mx-auto mb-3" />
              <h3 className="text-lg font-medium text-white mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Traditional Metals</h3>
              <p className="text-white/60 text-sm leading-relaxed">Hand-hammered copper, brass, and metal paper mache — shaped using techniques passed down through generations of Indian artisans.</p>
            </article>
            <article className="text-center">
              <Gem className="w-8 h-8 text-[#D4A843] mx-auto mb-3" />
              <h3 className="text-lg font-medium text-white mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Precision Engraving</h3>
              <p className="text-white/60 text-sm leading-relaxed">CNC laser cutting combined with hand-finishing ensures every pattern, mandala, and detail is crisp, consistent, and meaningful.</p>
            </article>
          </div>
        </div>
      </section>

      {/* Trust Banner — above footer */}
      <TrustBanner />
    </div>
  );
};

export default Home;
